import { styled } from '@mui/material/styles';
import { 
  Dialog, 
  DialogTitle as MuiDialogTitle, 
  DialogContent as MuiDialogContent,
  DialogActions as MuiDialogActions,
  Button as MuiButton,
  TextField,
  List,
  ListItem,
  IconButton,
  Box,
} from '@mui/material';

export const TemplateDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    width: '550px',
    maxWidth: '90vw',
    maxHeight: '80vh',
    height: 'auto',
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    display: 'flex',
    flexDirection: 'column',
  },
}));

export const DialogTitle = styled(MuiDialogTitle)(({ theme }) => ({
  padding: theme.spacing(3),
  fontWeight: 600,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

export const DialogContent = styled(MuiDialogContent)(({ theme }) => ({
  padding: theme.spacing(0, 0, 0, 0),
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  overflow: 'hidden',
}));

export const DialogActions = styled(MuiDialogActions)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  borderTop: `1px solid ${theme.palette.divider}`,
  justifyContent: 'flex-end',
}));

export const TemplateNameField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  width: '100%',
}));

export const TemplateDescriptionField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  width: '100%',
}));

export const ColumnList = styled(List)(({ theme }) => ({
  width: '100%',
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
  marginBottom: theme.spacing(2),
  maxHeight: '250px',
  overflowY: 'auto',
  '&:empty': {
    padding: theme.spacing(2),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontStyle: 'italic',
    color: theme.palette.text.secondary,
    '&:before': {
      content: '"Nenhuma coluna adicionada"',
    },
  },
}));

export const ColumnItem = styled(ListItem)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(1, 2),
  display: 'flex',
  alignItems: 'center',
  '&:last-child': {
    borderBottom: 'none',
  },
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

export const ColumnNameField = styled(TextField)({
  flex: 1,
});

export const DeleteColumnButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.error.main,
  '&:hover': {
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.dark,
  },
}));

export const AddColumnButton = styled(MuiButton)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  },
}));

export const SaveButton = styled(MuiButton)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  },
}));

export const CancelButton = styled(MuiButton)(({ theme }) => ({
  color: theme.palette.text.secondary,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

export const DeleteTemplateButton = styled(MuiButton)(({ theme }) => ({
  backgroundColor: theme.palette.error.main,
  color: theme.palette.error.contrastText,
  '&:hover': {
    backgroundColor: theme.palette.error.dark,
  },
}));

export const TemplateToolbarContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

export const TemplateToolbar = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

export const TemplateCardsContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  flex: 1,
  overflowY: 'auto',
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
    borderRadius: '10px',
    '&:hover': {
      background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
    },
  },
}));

export const TemplateCard = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  backgroundColor: theme.palette.background.paper,
  minHeight: '110px',
  display: 'flex',
  flexDirection: 'column',
  '&:hover': {
    boxShadow: theme.shadows[3],
    backgroundColor: theme.palette.action.hover,
  },
}));

export const CardContent = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
}));

export const ColumnPreview = styled(Box)(({ theme }) => ({
  display: 'flex',
  overflowX: 'auto',
  width: '100%',
  marginTop: theme.spacing(0.5),
  padding: theme.spacing(0.5, 0),
  scrollbarWidth: 'thin',
  scrollbarColor: `${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)'} transparent`,
  msOverflowStyle: 'none', /* IE and Edge */
  '&::-webkit-scrollbar': {
    height: '4px',
    backgroundColor: 'transparent',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
    margin: '0 2px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
    borderRadius: '10px',
    '&:hover': {
      background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
    },
  },
  '& > *': {
    flex: '0 0 auto',
    marginRight: theme.spacing(1),
    padding: theme.spacing(0.5, 1.5),
    borderRadius: '12px',
    fontSize: '0.75rem',
    whiteSpace: 'nowrap',
    minWidth: '60px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
  },
}));

export const NoTemplatesMessage = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  textAlign: 'center',
  color: theme.palette.text.secondary,
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

export const ActionsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
}));
